import { useMemo } from "react";
import { LayoutItem } from "../utils";
import { 
  TotalContractsCard, 
  ActiveContractsCard, 
  ExpiringContractsCard, 
  TotalValueCard 
} from "./StatCardWidgets";
import { ContractsTimelineChart } from "../ContractsTimelineChart";
import { ContractsDistributionChart } from "../ContractsDistributionChart";
import { RecentContractsTable } from "../RecentContractsTable";
import { UpcomingExpirationsTable } from "../UpcomingExpirationsTable";
import { ContractCostsByTypeWidget } from "../ContractCostsByTypeWidget";
import { FinancialResultWidget } from "../FinancialResultWidget";

export const useComponentMap = (
  userStats: any,
  contractsTimeline: any[],
  contractsDistribution: any[],
  recentContracts: any[],
  upcomingExpirations: any[],
  handlesVisible: boolean,
  onRemove: ((id: string) => void) | undefined,
  onResize: ((id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void) | undefined,
  defaultCurrency: string | null,
  defaultCurrencyId: string | null,
  selectedYear: number
) => {
  return useMemo(() => ({
    'total-contracts': (item: LayoutItem) => (
      <TotalContractsCard 
        item={item} 
        userStats={userStats} 
        handlesVisible={handlesVisible} 
        onRemove={onRemove} 
        onResize={onResize} 
      />
    ),
    'active-contracts': (item: LayoutItem) => (
      <ActiveContractsCard 
        item={item} 
        userStats={userStats} 
        handlesVisible={handlesVisible} 
        onRemove={onRemove} 
        onResize={onResize} 
      />
    ),
    'expiring-contracts': (item: LayoutItem) => (
      <ExpiringContractsCard 
        item={item} 
        userStats={userStats} 
        handlesVisible={handlesVisible} 
        onRemove={onRemove} 
        onResize={onResize} 
      />
    ),
    'user-total-value': (item: LayoutItem) => (
      <TotalValueCard 
        item={item} 
        userStats={userStats} 
        defaultCurrency={defaultCurrency}
        handlesVisible={handlesVisible} 
        onRemove={onRemove} 
        onResize={onResize} 
      />
    ),
    'contracts-timeline': (item: LayoutItem) => (
      <ContractsTimelineChart 
        contractsTimeline={contractsTimeline}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove ? (id) => onRemove(id) : undefined}
        size={item.size}
        onResize={onResize}
      />
    ),
    'contracts-distribution': (item: LayoutItem) => (
      <ContractsDistributionChart 
        contractsDistribution={contractsDistribution}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove ? (id) => onRemove(id) : undefined}
        size={item.size}
        onResize={onResize}
      />
    ),
    'recent-contracts': (item: LayoutItem) => (
      <RecentContractsTable
        recentContracts={recentContracts}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove ? (id) => onRemove(id) : undefined}
        size={item.size}
        onResize={onResize}
      />
    ),
    'upcoming-expirations': (item: LayoutItem) => (
      <UpcomingExpirationsTable
        upcomingExpirations={upcomingExpirations}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove ? (id) => onRemove(id) : undefined}
        size={item.size}
        onResize={onResize}
      />
    ),
    'contract-costs-by-type': (item: LayoutItem) => (
      <ContractCostsByTypeWidget
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove ? (id) => onRemove("contract-costs-by-type") : undefined}
        size={item.size}
        onResize={onResize}
        defaultCurrencySymbol={defaultCurrency}
        defaultCurrencyId={defaultCurrencyId}
      />
    ),
    'financial-result': (item: LayoutItem) => {
      console.log('Rendering financial-result with size:', item.size);
      return (
        <FinancialResultWidget
          rearrangingEnabled={handlesVisible}
          onRemove={onRemove}
          size={item.size}
          onResize={onResize}
          selectedYear={selectedYear}
        />
      );
    }
  }), [
    userStats, 
    contractsTimeline, 
    contractsDistribution, 
    recentContracts, 
    upcomingExpirations, 
    handlesVisible, 
    onRemove,
    onResize,
    defaultCurrency,
    defaultCurrencyId,
    selectedYear
  ]);
};
